import type { TFunction } from "i18next";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ConfirmationModal from "../../../../../parts/modals/ConfirmationModal";
import SvgIcon from "../../../../../parts/SvgIcon";
import { openMsbAgent } from "../../../../../utils/msb-agent";
import { frequencyLimit } from "../../../../../utils/questionnaire";
import {
  formatPeriod,
  type Insight,
  PROVIDER_LABELS,
  PROVIDER_MARKS,
  type Provider,
  providerUrl,
  SECTION_KEYS,
} from "./checkin-insights";
import { InsightSection, sectionText } from "./InsightSection";
import { useCheckinInsights } from "./useCheckinInsights";

const PICK_KEY = "checkinInsightsProvider";
const EXPLAINER_KEY = "checkinInsightsExplainer";

const readPick = () => {
  try {
    return localStorage.getItem(PICK_KEY);
  } catch {
    return null;
  }
};

const savePick = (provider: Provider) => {
  try {
    localStorage.setItem(PICK_KEY, provider);
  } catch {
    // Losing the pick only means the picker asks again.
  }
};

const copyText = (text: string) =>
  navigator.clipboard?.writeText(text).catch(() => {});

const Icon = ({ src, className = "" }: { src: string; className?: string }) => (
  <SvgIcon
    src={`/img/new-icons/${src}.svg`}
    useCurrentColor
    classNames={`ci-icon ${className}`}
  />
);

/** Flips a button label to "Copied" for a moment. */
const CopyButton = ({ text, label }: { text: string; label: string }) => {
  const { t } = useTranslation("athlete-portal");
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(timer);
  }, [copied]);
  return (
    <button
      type="button"
      data-button-type="secondary"
      data-icon
      sz="small"
      onClick={() => {
        copyText(text);
        setCopied(true);
      }}
    >
      <Icon src={copied ? "tick-circle-outline-white" : "copy"} />
      {copied ? t("checkinInsights.copied") : label}
    </button>
  );
};

export const useCheckinInsightsController = ({
  selectedAnswer,
  athlete,
  activeUser,
}) => {
  const isCoachView =
    !!activeUser?.permissions?.includes("coach") &&
    !!athlete &&
    String(athlete._id) !== String(activeUser._id);
  const checkinId = isCoachView && selectedAnswer ? selectedAnswer._id : null;
  const state = useCheckinInsights(checkinId);
  const { t } = useTranslation("athlete-portal");

  const [pickerMode, setPickerMode] = useState<"run" | "change" | null>(null);
  const [pick, setPick] = useState<string | null>(readPick);
  const [error, setError] = useState<string | null>(null);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [explainerHidden, setExplainerHidden] = useState(
    !!activeUser?.welcomeVideos?.[EXPLAINER_KEY]
  );

  const days = selectedAnswer
    ? frequencyLimit[selectedAnswer.questionnaire?.questionnaireFrequency] || 7
    : 7;
  const end = selectedAnswer
    ? moment(String(selectedAnswer.utcDate), "YYYYMMDD")
    : null;
  const period = end
    ? formatPeriod(
        end
          .clone()
          .subtract(days - 1, "days")
          .format("YYYY-MM-DD"),
        end.format("YYYY-MM-DD")
      )
    : "";
  const clientName = athlete?.name || "";

  const buildPrompt = useCallback(
    (requestId: string) =>
      t("checkinInsights.prompt", { name: clientName, period, requestId }),
    [t, clientName, period]
  );

  const providers = state.connections ?? [];
  const provider: Provider | null =
    providers.length === 1
      ? providers[0]
      : (providers.find((candidate) => candidate === pick) ?? null);

  const run = useCallback(
    async (trigger: Element | null, chosen?: Provider) => {
      setError(null);
      setPopupBlocked(false);
      if (!providers.length) {
        openMsbAgent(trigger, activeUser);
        return;
      }
      if (chosen) {
        savePick(chosen);
        setPick(chosen);
      }
      const target = chosen ?? provider;
      if (!target) {
        setPickerMode("run");
        return;
      }
      setPickerMode(null);
      // window.open must run inside the click or pop-up blockers drop it; the
      // URL needs the new request id, so the tab is pointed at it afterwards.
      const tab = window.open("", "_blank");
      if (tab) tab.opener = null;
      try {
        const request = await state.start(target);
        const url = providerUrl(target, buildPrompt(request.id));
        if (tab) tab.location.href = url;
        else setPopupBlocked(true);
      } catch (runError) {
        tab?.close();
        setError(runError.message || t("checkinInsights.startFailed"));
      }
    },
    [providers, provider, activeUser, state.start, buildPrompt, t]
  );

  const changePick = (chosen: Provider) => {
    savePick(chosen);
    setPick(chosen);
    setPickerMode(null);
  };

  const hideExplainer = () => {
    setExplainerHidden(true);
    fetch("/actions/user/video-shown", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type: EXPLAINER_KEY }),
    }).catch(() => {});
  };

  return {
    ...state,
    enabled: !!checkinId,
    clientName,
    period,
    providers,
    provider,
    pickerMode,
    setPickerMode,
    changePick,
    run,
    buildPrompt,
    error,
    popupBlocked,
    explainerHidden,
    hideExplainer,
  };
};

type Controller = ReturnType<typeof useCheckinInsightsController>;

const HelpTip = () => {
  const { t } = useTranslation("athlete-portal");
  return (
    <span className="ci-help">
      <button
        type="button"
        className="ci-help-btn"
        aria-label={t("checkinInsights.helpLabel")}
      >
        <Icon src="question-circle" />
      </button>
      <span className="ci-tip" role="tooltip">
        <b>{t("checkinInsights.title")}</b>
        {t("checkinInsights.tooltip")}
      </span>
    </span>
  );
};

const Picker = ({ controller }: { controller: Controller }) => {
  const { t } = useTranslation("athlete-portal");
  const ref = useRef<HTMLDivElement>(null);
  const { pickerMode, setPickerMode, providers, provider, run, changePick } =
    controller;

  useEffect(() => {
    if (!pickerMode) return undefined;
    const onClick = (event: MouseEvent) => {
      if (!ref.current?.parentElement?.contains(event.target as Node))
        setPickerMode(null);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPickerMode(null);
    };
    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [pickerMode, setPickerMode]);

  if (!pickerMode) return null;
  return (
    <div
      className="ci-picker"
      ref={ref}
      role="dialog"
      aria-label={t("checkinInsights.pickerTitle")}
    >
      <div className="ttl">{t("checkinInsights.pickerTitle")}</div>
      <div className="sub">{t("checkinInsights.pickerBody")}</div>
      {providers.map((option) => (
        <button
          type="button"
          key={option}
          className="option"
          onClick={(event) =>
            pickerMode === "run"
              ? run(event.currentTarget, option)
              : changePick(option)
          }
        >
          <span className="ai-mark">{PROVIDER_MARKS[option]}</span>
          <span className="t">
            {t("checkinInsights.openIn", { ai: PROVIDER_LABELS[option] })}
          </span>
          <span className={`ci-radio${provider === option ? " on" : ""}`} />
        </button>
      ))}
    </div>
  );
};

/** Header action: Ask / Waiting + Cancel / Insights ready + Regenerate / Try again. */
export const InsightsAction = ({
  controller,
  onRegenerate,
}: {
  controller: Controller;
  onRegenerate: () => void;
}) => {
  const { t } = useTranslation("athlete-portal");
  const { enabled, loaded, view, request, providers, provider, run } =
    controller;
  if (!enabled) return null;

  const ai = PROVIDER_LABELS[request?.provider ?? provider] ?? "";
  const busy = !loaded || controller.connections === null;
  const caption =
    providers.length > 1 && provider ? (
      <span className="ci-cap">
        {t("checkinInsights.opensIn", { ai: PROVIDER_LABELS[provider] })} ·{" "}
        <button
          type="button"
          className="ci-link"
          onClick={(event) => {
            event.stopPropagation();
            controller.setPickerMode(
              controller.pickerMode === "change" ? null : "change"
            );
          }}
        >
          {t("checkinInsights.change")}
        </button>
      </span>
    ) : provider ? (
      <span className="ci-cap">
        {t("checkinInsights.opensIn", { ai: PROVIDER_LABELS[provider] })}
      </span>
    ) : null;

  let row: React.ReactNode;
  if (view === "waiting" || view === "regen") {
    return (
      <div className="ci-slot">
        <div className="row-b">
          <button
            type="button"
            data-button-type="secondary"
            data-icon
            disabled
            aria-live="polite"
            className="ci-waiting-btn"
          >
            <span className="ci-spin" />
            {view === "regen"
              ? t("checkinInsights.regenerating")
              : t("checkinInsights.waiting")}
          </button>
          <button
            type="button"
            data-button-type="secondary"
            data-action="cancel"
            onClick={() => controller.cancel()}
          >
            {t("checkinInsights.cancel")}
          </button>
        </div>
        <span className="ci-cap">
          {t("checkinInsights.startedIn", {
            time: moment(request.createdAt).format("LT"),
            ai,
          })}
        </span>
      </div>
    );
  }
  if (view === "ready") {
    row = (
      <>
        <span className="ci-pill success">
          <Icon src="tick-circle-outline-white" />
          {t("checkinInsights.ready")}
        </span>
        <button
          type="button"
          data-button-type="secondary"
          data-icon
          onClick={(event) => {
            if (!providers.length) openMsbAgent(event.currentTarget);
            else onRegenerate();
          }}
        >
          <Icon src="vuesax-linear-refresh-2" />
          {t("checkinInsights.regenerate")}
        </button>
      </>
    );
  } else {
    const retry = view === "nohear" || view === "failed" || view === "expired";
    row = (
      <button
        type="button"
        data-button-type="primary"
        data-icon
        disabled={busy}
        onClick={(event) => {
          event.stopPropagation();
          run(event.currentTarget);
        }}
      >
        <Icon src="magic-star" />
        {retry ? t("checkinInsights.tryAgain") : t("checkinInsights.ask")}
      </button>
    );
  }

  return (
    <div className="ci-slot">
      <div className="row-b">
        {row}
        <HelpTip />
      </div>
      {caption}
      <Picker controller={controller} />
    </div>
  );
};

const PrivatePill = () => {
  const { t } = useTranslation("athlete-portal");
  return (
    <span className="ci-pill default">
      <Icon src="lock" />
      {t("checkinInsights.private")}
    </span>
  );
};

const Head = ({
  icon = "magic-star",
  title,
  pill = null,
  meta = null,
  tools = null,
}: {
  icon?: string;
  title: React.ReactNode;
  pill?: React.ReactNode;
  meta?: React.ReactNode;
  tools?: React.ReactNode;
}) => (
  <div className="ci-head">
    <span className="spark">
      <Icon src={icon} />
    </span>
    <div className="grow">
      <div className="ci-title">
        <h3>{title}</h3>
        {pill}
      </div>
      {meta && <div className="ci-meta">{meta}</div>}
    </div>
    {tools && <div className="ci-tools">{tools}</div>}
  </div>
);

const allText = (
  insight: Insight,
  controller: Controller,
  t: TFunction<"athlete-portal">
) =>
  [
    `${t("checkinInsights.title")} · ${controller.clientName} · ${controller.period}`,
    t("checkinInsights.generatedBy", {
      ai: PROVIDER_LABELS[insight.provider],
      date: moment(insight.generatedAt).format("lll"),
    }),
    "",
    SECTION_KEYS.map((key) => sectionText(key, insight.sections[key], t)).join(
      "\n\n"
    ),
  ].join("\n");

/** The explainer, the waiting/problem cards and the insights themselves. */
export const InsightsPanel = ({ controller }: { controller: Controller }) => {
  const { t } = useTranslation("athlete-portal");
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [collapsed, setCollapsed] = useState(false);
  const { enabled, loaded, view, insight, request, clientName } = controller;
  if (!enabled || !loaded) return null;

  const ai = PROVIDER_LABELS[request?.provider ?? controller.provider] ?? "";
  const prompt = request ? controller.buildPrompt(request.id) : "";
  const startedAt = request ? moment(request.createdAt).format("LT") : "";
  const cards: React.ReactNode[] = [];

  if (controller.error) {
    cards.push(
      <div className="ci-card edge-danger" key="error" role="alert">
        <Head
          icon="info-circle"
          title={t("checkinInsights.startFailed")}
          meta={controller.error}
        />
      </div>
    );
  }

  if (view === "empty" && !controller.explainerHidden) {
    cards.push(
      <div className="ci-card" key="explainer">
        <Head
          title={t("checkinInsights.title")}
          pill={<PrivatePill />}
          meta={t("checkinInsights.explainerMeta")}
          tools={
            <button
              type="button"
              data-button-type="text"
              sz="small"
              onClick={controller.hideExplainer}
            >
              {t("checkinInsights.hide")}
            </button>
          }
        />
        <div className="ci-empty">
          <div>
            <p>{t("checkinInsights.explainerBody", { name: clientName })}</p>
            <div className="what">
              {SECTION_KEYS.map((key) => (
                <span className="ci-pill default" key={key}>
                  {t(`checkinInsights.sections.${key}`)}
                </span>
              ))}
            </div>
          </div>
          <div className="reads">
            <div className="overline">{t("checkinInsights.readsTitle")}</div>
            <ul>
              {["reads1", "reads2", "reads3", "reads4", "reads5"].map((key) => (
                <li key={key}>
                  {t(`checkinInsights.${key}`, { name: clientName })}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  if (view === "waiting") {
    cards.push(
      <div className="ci-card" key="waiting">
        <Head
          title={t("checkinInsights.preparingTitle")}
          pill={<PrivatePill />}
          meta={
            controller.popupBlocked
              ? t("checkinInsights.popupBlocked", { ai })
              : t("checkinInsights.preparingMeta", { ai })
          }
          tools={
            <CopyButton text={prompt} label={t("checkinInsights.copyPrompt")} />
          }
        />
        <ol className="ci-steps">
          <li>
            <span className="st done">
              <Icon src="tick-circle-outline-white" />
            </span>
            <span className="l">{t("checkinInsights.step1", { ai })}</span>
            <span className="tm">{startedAt}</span>
          </li>
          <li className={request.fetchedAt ? "" : "pending"}>
            <span className="st">
              {request.fetchedAt ? (
                <span className="ci-spin" />
              ) : (
                <span className="ci-dot" />
              )}
            </span>
            <span className="l">
              {t("checkinInsights.step2", { name: clientName })}
            </span>
            <span className="tm">{t("checkinInsights.fewMinutes")}</span>
          </li>
          <li className="pending">
            <span className="st">
              <span className="ci-dot" />
            </span>
            <span className="l">{t("checkinInsights.step3")}</span>
          </li>
        </ol>
      </div>
    );
  }

  if (view === "nohear") {
    cards.push(
      <div className="ci-card edge-warning" key="nohear">
        <Head
          icon="info-circle"
          title={t("checkinInsights.noHearTitle")}
          meta={t("checkinInsights.noHearMeta", { ai, time: startedAt })}
        />
        <div className="ci-prob">
          <ol>
            <li>{t("checkinInsights.noHear1", { ai })}</li>
            <li>{t("checkinInsights.noHear2", { ai })}</li>
            <li>{t("checkinInsights.noHear3", { ai })}</li>
          </ol>
          <div className="foot">
            <CopyButton text={prompt} label={t("checkinInsights.copyPrompt")} />
            <button
              type="button"
              data-button-type="primary"
              sz="small"
              onClick={(event) => controller.run(event.currentTarget)}
            >
              {t("checkinInsights.tryAgain")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === "failed" || view === "expired") {
    const expired = view === "expired";
    cards.push(
      <div className="ci-card edge-danger" key="problem">
        <Head
          icon="info-circle"
          title={
            expired
              ? t("checkinInsights.expiredTitle")
              : t("checkinInsights.failedTitle")
          }
          meta={
            expired
              ? t("checkinInsights.expiredMeta", {
                  ai,
                  date: moment(request.createdAt).format("lll"),
                })
              : t("checkinInsights.failedMeta")
          }
        />
        <div className="ci-prob">
          <div className="foot">
            <button
              type="button"
              data-button-type="primary"
              sz="small"
              onClick={(event) => controller.run(event.currentTarget)}
            >
              {t("checkinInsights.tryAgain")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (insight) {
    const setAll = (value: boolean) =>
      setOpen(Object.fromEntries(SECTION_KEYS.map((key) => [key, value])));
    cards.push(
      <div className={`ci-card${collapsed ? " closed" : ""}`} key="insight">
        <Head
          title={t("checkinInsights.title")}
          pill={<PrivatePill />}
          meta={t("checkinInsights.meta", {
            ai: PROVIDER_LABELS[insight.provider],
            date: moment(insight.generatedAt).format("lll"),
            cutoff: moment(insight.dataCutoff).format("ll"),
          })}
          tools={
            <>
              <CopyButton
                text={allText(insight, controller, t)}
                label={t("checkinInsights.copyAll")}
              />
              <button
                type="button"
                data-button-type="secondary"
                data-icon-only
                sz="small"
                className="ci-collapse"
                aria-label={t("checkinInsights.collapse")}
                aria-expanded={!collapsed}
                onClick={() => setCollapsed(!collapsed)}
              >
                <Icon src="chevron-down" />
              </button>
            </>
          }
        />
        <div className="ci-body">
          {view === "regen" && (
            <div className="ci-bar">
              <span className="ci-spin" />
              <span className="grow">
                {t("checkinInsights.regenBar", { ai })}
              </span>
            </div>
          )}
          <div className="ci-sub">
            <span className="overline">
              {t("checkinInsights.sectionCount", {
                count: SECTION_KEYS.length,
              })}
            </span>
            <span>
              <button
                type="button"
                data-button-type="text"
                sz="small"
                onClick={() => setAll(true)}
              >
                {t("checkinInsights.expandAll")}
              </button>
              <button
                type="button"
                data-button-type="text"
                sz="small"
                onClick={() => setAll(false)}
              >
                {t("checkinInsights.collapseAll")}
              </button>
            </span>
          </div>
          <div className="ci-secs">
            {SECTION_KEYS.map((key) => (
              <InsightSection
                key={key}
                sectionKey={key}
                section={insight.sections[key]}
                open={open[key] !== false}
                onToggle={() =>
                  setOpen({ ...open, [key]: open[key] === false })
                }
                onSave={(content) => controller.saveSection(key, content)}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return cards.length ? <div className="ci-panel">{cards}</div> : null;
};

/** Regenerate confirm; warns when the coach's edits would be replaced. */
export const RegenerateModal = ({
  controller,
  isOpen,
  onClose,
}: {
  controller: Controller;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation("athlete-portal");
  const hasEdits = SECTION_KEYS.some(
    (key) => controller.insight?.sections[key]?.edited
  );
  return (
    <ConfirmationModal
      isOpen={isOpen}
      title={t("checkinInsights.regenTitle")}
      contentText={`${t("checkinInsights.regenBody")}${hasEdits ? ` ${t("checkinInsights.regenEdits")}` : ""}`}
      cancelButtonText={t("checkinInsights.cancel")}
      cancelButtonAction={onClose}
      confirmButtonText={t("checkinInsights.regenConfirm")}
      confirmButtonAction={(event) => {
        onClose();
        controller.run(event.currentTarget);
      }}
      confirmSuccess
    />
  );
};
